/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { AfterViewInit, Component, ElementRef, EventEmitter, Inject, NgZone, OnDestroy, OnInit, viewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MemberInfoDialogData } from '@app/app/page-modules/admin/components/members/model/members.model';
import { PlaylistViewState } from '@app/app/page-modules/playlist/store/view/playlist-view.state';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { v3DialogConfig } from '@app/app/shared/helpers/dialog/v3-dialog-config';
import { Playlist, Resource } from '@app/app/shared/models';
import { MembersBooleanSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import { MemberGroup, Members } from '@app/app/shared/models/admin/members.model';
import { TOAST_NOTIFICATION_SERVICE, ToastService } from '@app/app/shared/services/toast-notifications/toast-service';
import { LoadableState } from '@app/app/shared/store';
import { LucideCopy, LucideExternalLink, LucideMessageSquare, LucideX } from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Select } from '@ngxs/store';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ptl-v3-member-info-dialog',
  templateUrl: './v3-member-info-dialog.component.html',
  styleUrls: ['./v3-member-info-dialog.component.scss'],
})
export class V3MemberInfoDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly LucideX = LucideX;
  readonly LucideExternalLink = LucideExternalLink;
  readonly LucideMessageSquare = LucideMessageSquare;
  readonly LucideCopy = LucideCopy;
  private closeBtnElement = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');

  member: Members;
  isPlaylistPage: boolean;
  isCardPage: boolean;
  parentUrl: string;
  programUid: string;
  programTitle: string;
  hasTicketEnrollment = false;

  groups: MemberGroup[] = [];
  roles: string[] = [];
  initials: string;

  readonly maxVisiblePills = 10;
  visibleRoles: string[] = [];
  hiddenRolesCount = 0;
  visibleGroups: MemberGroup[] = [];
  hiddenGroupsCount = 0;

  enroll = new EventEmitter<void>();
  unenroll = new EventEmitter<void>();

  @Select(ResourceAdminState.resource)
  resource$: Observable<Resource>;

  @Select(PlaylistViewState.playlistState)
  playlist$: Observable<LoadableState<Playlist>>;

  private booleanSearchRequest: MembersBooleanSearchRequest;
  private subscriptionEnd$ = new EventEmitter<void>();
  private playlistUid: string;
  private cardUid: string;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<V3MemberInfoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: MemberInfoDialogData,
    @Inject(TOAST_NOTIFICATION_SERVICE) private toastService: ToastService,
    private transloco: TranslocoService,
  ) {
    if (data) {
      this.member = this.data.member;
      this.isPlaylistPage = this.data.isPlaylistPage;
      this.isCardPage = this.data.isCardPage;
      this.parentUrl = this.data.parentUrl;
      this.booleanSearchRequest = this.data.query;
      this.programUid = this.data.programUid;
      this.programTitle = this.data.programTitle;
      this.hasTicketEnrollment = !!this.data.hasTicketEnrollment;
    }
  }

  /** The drawer is most often opened to get the address out, so the value itself is the control. */
  onCopy(value?: string): void {
    if (!value) {
      return;
    }
    navigator.clipboard
      .writeText(value)
      .then(() => this.toastService.showSuccess(this.transloco.translate('translations.membersNew.info.emailCopied')))
      .catch(() => this.toastService.showFail(this.transloco.translate('translations.errors.unknownError')));
  }

  ngOnInit(): void {
    this.groups = this.member?.groups ?? [];
    this.roles = (this.member?.roles ?? []).filter((role) => !!role);
    this.initials = this.getInitials(this.member);

    this.visibleRoles = this.roles.slice(0, this.maxVisiblePills);
    this.hiddenRolesCount = Math.max(0, this.roles.length - this.maxVisiblePills);
    this.visibleGroups = this.groups.slice(0, this.maxVisiblePills);
    this.hiddenGroupsCount = Math.max(0, this.groups.length - this.maxVisiblePills);

    this.playlist$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((playlist) => {
      if (playlist && playlist.data) {
        this.playlistUid = playlist.data._id;
      }
    });

    this.resource$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((card) => {
      if (card) {
        this.cardUid = card._id;
      }
    });
  }

  ngAfterViewInit(): void {
    this.closeBtnElement()?.nativeElement?.focus();
  }

  ngOnDestroy(): void {
    this.subscriptionEnd$.emit();
  }

  getMemberCompletionStatus(member: Members): 'IN_PROGRESS' | 'COMPLETED' | 'NOT_STARTED' {
    if (member.cardStatus === 'COMPLETED' || member.playlistStatus === 'COMPLETED') {
      return 'COMPLETED';
    } else if (member.cardStatus === 'IN_PROGRESS' || member.playlistStatus === 'IN_PROGRESS') {
      return 'IN_PROGRESS';
    }
    return 'NOT_STARTED';
  }

  isEnrolled(): boolean {
    return !!this.member?.enrollment?.registeredOn;
  }

  openMemberDetailPage(userUid: string): void {
    this.closeDialog();

    if (this.programUid) {
      const returnUrl = this.router.url;
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, `/admin/members/${userUid}/user-record`, {
        state: { returnUrl, returnLabel: 'Back' },
      });
      return;
    }

    const url = `${this.parentUrl}/${userUid}/user-record`;

    if (this.isPlaylistPage || this.isCardPage) {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, url.toString(), null);
    } else {
      window.open(url.toString(), '_blank');
    }
  }

  // Imported on demand: the dialog pulls in TiptapModule, which imports SharedModule — the module that
  // declares this component. A static import closes that cycle and blanks the app at startup.
  async openSendMessageDialog(): Promise<void> {
    this.closeDialog();
    const { V3SendMessageDialogComponent } = await import(
      '../../v3-member-dialogs/v3-send-message-dialog/v3-send-message-dialog.component'
    );

    this.ngZone.run(() => {
      this.dialog.open(
        V3SendMessageDialogComponent,
        v3DialogConfig({
          width: '46.875rem',
          data: {
            checkedMemberIds: [this.member._id],
            query: this.booleanSearchRequest.query,
            allMembers: false,
            filteredCount: 1,
            playlistUid: this.programUid || this.playlistUid,
            cardUid: this.cardUid,
          },
        }),
      );
    });
  }

  closeDialog(): void {
    const overlayPane = document.querySelector('.f_slide-in-dialog');

    if (overlayPane) {
      overlayPane.classList.add('slide-out');
    }

    setTimeout(() => {
      this.dialogRef.close();
    }, 300);
  }

  private getInitials(member: Members): string {
    return (((member?.firstName ?? '')[0] ?? '') + ((member?.lastName ?? '')[0] ?? '')).toUpperCase();
  }
}
